import moment from 'moment-timezone';

import {
  AgentApprovalCategory,
  AgentApprovalResponse,
  AgentType,
  ChatType,
  CitationGenerationMode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatFileUpload, UserContext} from 'llm/chat-library/ChatLibraryTypes';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import * as Net from 'util/Net';

import {doesAgentTypeSupportWorkspaceContext} from './sidepanel/ChatTypeUtil';

interface CreateTextMessageBlockInput {
  type: 'text';
  text: string;
}
interface CreateFileMessageBlockInput {
  type: 'file';
  source: {type: 's3'; s3Location: string};
  filename: string;
  mediaType: string;
}

interface PublicDataChatSourceInput {
  type: 'public_data';
}

export interface McpServerChatSourceInput {
  type: 'mcp_server';
  serverId: string;
}

type CreateMessageBlockInput = CreateTextMessageBlockInput | CreateFileMessageBlockInput;
export type ChatSourceInput = PublicDataChatSourceInput | McpServerChatSourceInput;

/** Keep in sync with Benchling.AgentApprovalDecisionInput. */
export interface AgentApprovalDecisionInput {
  category: AgentApprovalCategory;
  response: AgentApprovalResponse;
  /** Id of the assistant request message this decision answers (the API resolves it to requestEvent). */
  requestEventId: string;
}

/**
 * Build the write-actions approval decision a user message carries in response to a request:
 * APPROVED_THIS_CHAT (Allow), EXPLICITLY_DENIED (Deny), or IMPLICITLY_DENIED (a typed reply instead
 * of answering). ``requestEventId`` is the assistant request message the decision answers.
 */
export function buildAgentApprovalDecision(
  response: AgentApprovalResponse,
  requestEventId: string
): AgentApprovalDecisionInput {
  return {category: AgentApprovalCategory.WRITE_ACTIONS, response, requestEventId};
}

/** Keep in sync with Benchling.CreateDisplayMessageChatEvent */
export interface CreateDisplayMessageChatEvent {
  chatSessionId: string;
  agentType: AgentType;
  messageBlocks: Array<CreateMessageBlockInput>;
  userChatContext: UserContext;
  sources?: Array<ChatSourceInput>;
  citationGenerationMode?: CitationGenerationMode;
  retry?: boolean;
  /**
   * The user's agent-approval response carried by this message, if any (see
   * LLM_ENABLE_AGENT_APPROVALS). Set by the approval request's "Approve" button. Omit when the
   * message carries no decision.
   */
  agentApprovalDecision?: AgentApprovalDecisionInput;
}

interface CreateChatInput {
  type: ChatType;
  forAgentType?: AgentType;
}

/**
 * Options for creating a DATA_CAPTURE chat session.
 */
export interface DataCaptureCreateOptions {
  /** Optional ID of an existing entry to target (for Compose over Entries). */
  targetEntryId?: string;
}

/**
 * Options for creating a CO_SCIENTIST chat session.
 */
export interface CoScientistCreateOptions {
  agentWorkspaceFolderId?: string;
  /**
   * When true, the chat is routed through the background assistant queue. Gated by
   * LLM_ENABLE_LEGACY_DATA_IMPORT_AGENT_QUEUE flag.
   */
  useBackgroundAgentQueue?: boolean;
}

/**
 * Options for creating an ASSISTANT chat session.
 */
export interface AssistantCreateOptions {
  /** Optional ID of an existing entry to target. */
  targetEntryId?: string;
}

export interface PipelinesCustomCodeWriterCreateOptions {
  /** ID of the pipeline step the chat is writing Custom Code for. */
  pipelineStepId: string;
  /** ID of the parent pipeline. */
  pipelineId: string;
}

/**
 * Options for creating a chat session, keyed by AgentType.
 */
export interface CreateChatOptions {
  [AgentType.DATA_CAPTURE]?: DataCaptureCreateOptions;
  [AgentType.CO_SCIENTIST]?: CoScientistCreateOptions;
  [AgentType.ASSISTANT]?: AssistantCreateOptions;
  [AgentType.PIPELINES_CUSTOM_CODE_WRITER]?: PipelinesCustomCodeWriterCreateOptions;
}

/**
 * Create a chat session.
 */
export function createChat(data: CreateChatInput): Promise<{id: string}> {
  return Net.postJSONPromise({url: '/1/api/chats', data});
}

/**
 * Append a message to a chat session.
 */
export function appendDisplayMessageToChatSession(
  data: CreateDisplayMessageChatEvent
): Promise<{id: string}> {
  return Net.postJSONPromise({url: '/1/api/chats/messages', data});
}

/**
 * Request that the agent stop responding to an in-progress user display message.
 */
export async function interruptChatSession(
  chatSessionId: string,
  chatEventId: string
): Promise<{id: string}> {
  const response = await Net.postJSONPromise({
    url: `/1/api/chats/${chatSessionId}:interrupt`,
    data: {chatEventId},
  });
  return response;
}

/**
 * Pure function for determining workspace context IDs.
 * Accepts store values as params for testability.
 */
export function getWorkspaceContextIdsFromStore(
  agentType: AgentType,
  stores: {isSidepanelExpanded: boolean; activeWorkspaceId: string | null}
): Array<string> {
  if (
    !doesAgentTypeSupportWorkspaceContext(agentType) ||
    stores.isSidepanelExpanded ||
    !stores.activeWorkspaceId
  ) {
    return [];
  }
  return [stores.activeWorkspaceId];
}

export function getContextIdsFromActiveWorkspace(agentType: AgentType): Array<string> {
  return getWorkspaceContextIdsFromStore(agentType, {
    isSidepanelExpanded: SidepanelStore.getIsExpanded(),
    activeWorkspaceId: WorkspaceStore.activeId,
  });
}

/**
 * Helper function to get the message block input for a message.
 *
 * @deprecated - construct {@link CreateMessageBlockInput}s directly instead.
 */
export function getMessageBlockInput(message: {
  text: string;
  fileUploads: Array<ChatFileUpload>;
}): Array<CreateMessageBlockInput> {
  return [
    {
      type: 'text' as const,
      text: message.text,
    },
    ...message.fileUploads.map((fileUpload) => ({
      type: 'file' as const,
      source: {type: 's3' as const, s3Location: fileUpload.s3Location},
      filename: fileUpload.filename,
      mediaType: fileUpload.mediaType,
    })),
  ];
}

export async function createChatSession(
  chatType: ChatType,
  createOptions: CreateChatOptions = {},
  forAgentType?: AgentType
): Promise<string> {
  const response = await Net.postJSONPromise({
    url: '/1/api/chats',
    data: {type: chatType, createOptions, forAgentType},
  });
  return response.id;
}

export {CitationGenerationMode};
/**
 * @param contextIds - consider using getContextIdsFromActiveWorkspace.
 */
export function getUserChatContext(data: {contextIds: Array<string>}): UserContext {
  return {
    ids: data.contextIds,
    // intentionally en-US to match the rest of our prompting
    userLocalTime: new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }),
    userLocalDate: moment().format('YYYY-MM-DD'),
    userTimezone: moment.tz.guess(),
  };
}

/**
 * Update a chat session.
 */
export function updateChatSession(chatId: string, data: {name: string}): Promise<{modifiedAt: string}> {
  return Net.patchJSONPromise({url: `/1/api/chats/${chatId}`, data});
}

/**
 * Get the download URL for a file uploaded to a chat session.
 */
export function getChatFileDownloadUrl(chatSessionId: string, externalFileId: string): string {
  return `/1/api/chats/${chatSessionId}/uploaded-files/${externalFileId}:download`;
}

/**
 * Get the preview URL for a file uploaded to a chat session.
 */
export function getChatFilePreviewUrl(chatSessionId: string, externalFileId: string): string {
  return `/1/api/chats/${chatSessionId}/uploaded-files/${externalFileId}:preview`;
}

/**
 * Fetch a presigned S3 URL for a chat uploaded file.
 *
 * The preview endpoint normally returns a 302 redirect to S3, but cross-origin
 * redirects from fetch() fail due to CORS. This requests the URL as JSON instead
 * and returns the presigned URL for the caller to fetch directly.
 */
export async function fetchSignedFileUrl(chatSessionId: string, externalFileId: string): Promise<string> {
  const previewUrl = getChatFilePreviewUrl(chatSessionId, externalFileId);
  const {url} = await Net.getJSONPromise(previewUrl, {format: 'json'});
  return url;
}
